import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import { noop, curry } from 'lodash';
import { initializeApp } from 'firebase/app';
import { getFirestore, connectFirestoreEmulator, onSnapshot, doc, updateDoc, getDoc, setDoc, collection, getDocs, where, query, runTransaction, arrayUnion } from 'firebase/firestore';
import { getAuth, connectAuthEmulator, onAuthStateChanged, updateProfile, signInWithCustomToken, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification, EmailAuthProvider, FacebookAuthProvider, GithubAuthProvider, GoogleAuthProvider, TwitterAuthProvider, OAuthProvider, SAMLAuthProvider, linkWithRedirect, linkWithPopup, getAdditionalUserInfo, signInWithRedirect, signInWithPopup } from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import jwtDecode from 'jwt-decode';
import { useGoogleLogin } from 'react-google-login';

/*
 * useInitFirebaseCtx hook is for library internal usage only. When called
 * useInitFirebaseCtx will return a memoized set of Firebase related
 * authorization datas and functionality.
 */
const useInitFirebaseCtx = ({ config, emulate, authCookieName, }) => {
    const [app, setApp] = useState(null);
    const [auth, setAuth] = useState(null);
    const [firestore, setFirestore] = useState(null);
    const [storage, setStorage] = useState(null);
    const [user, setUser] = useState(null);
    const [authHasLoaded, setAuthHasLoaded] = useState(false);
    useEffect(() => {
        const initializedApp = initializeApp(config);
        const initializedAuth = getAuth(initializedApp);
        const initializedFirestore = getFirestore(initializedApp);
        const initializedStorage = getStorage(initializedApp);
        if (emulate) {
            const host = "localhost";
            connectAuthEmulator(initializedAuth, `http://${host}:9099`);
            connectFirestoreEmulator(initializedFirestore, host, 8080);
        }
        setApp(initializedApp);
        setAuth(initializedAuth);
        setFirestore(initializedFirestore);
        setStorage(initializedStorage);
    }, [setApp, setAuth, setFirestore, setStorage, config, emulate]);
    useEffect(() => {
        if (!auth)
            return;
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            // This callback is called after auth is initialized if there is a valid
            // user or not. By tracking if auth has finished trying to authenticate
            // a user or not we can better handle what we display to a user while we
            // try to authenticate them.
            setAuthHasLoaded(true);
            // If the user is associated with a tenant, we need the auth to operate
            // against that tenant
            if (user === null || user === void 0 ? void 0 : user.tenantId) {
                auth.tenantId = user.tenantId;
            }
            setUser(user);
        });
        return () => unsubscribe();
    }, [auth, setUser, setAuthHasLoaded]);
    return useMemo(() => {
        return {
            app,
            auth,
            user,
            emulate,
            setUser,
            firestore,
            storage,
            authHasLoaded,
            authCookieName,
        };
    }, [
        app,
        auth,
        user,
        emulate,
        setUser,
        firestore,
        storage,
        authHasLoaded,
        authCookieName,
    ]);
};

const initialState$1 = {
    app: null,
    auth: null,
    user: null,
    setUser: noop,
    emulate: false,
    firestore: null,
    storage: null,
    authHasLoaded: false,
    authCookieName: "",
};
const SUBContext = React.createContext(initialState$1);
const SUBContextProvider = ({ config, children, googleClientId, emulate = false, authCookieName, googleHostedDomain, }) => {
    const subState = useInitFirebaseCtx({ emulate, config, authCookieName });
    return React.createElement(SUBContext.Provider, { value: subState }, children);
};

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const useSUBContext = () => {
    return useContext(SUBContext);
};

const usersCollectionKey = "users";
const organizationsCollectionKey = "organizations";
const membersCollectionKey = "members";

/*
 * useObserveDocument will register the given `onUpdate` callback with the
 * specified document in Firestore via the `onSnapshot` function. Any observer
 * will be unsubscribed from when this hook is updated.
 * If no `docKey` is provided or no `firestore` instance is instantiated then
 * the `useEffect` call will return early.
 */
const useObserveDocument = ({ docKey, onUpdate, collectionKey, includeMetadataChanges = false, }) => {
    const { firestore } = useSUBContext();
    const handleUpdate = useCallback((doc) => {
        onUpdate((doc === null || doc === void 0 ? void 0 : doc.data()) || null);
    }, [onUpdate]);
    useEffect(() => {
        if (!firestore || !docKey)
            return;
        const unsub = onSnapshot(doc(firestore, collectionKey, docKey), { includeMetadataChanges }, handleUpdate);
        return unsub;
    }, [
        docKey,
        firestore,
        handleUpdate,
        collectionKey,
        includeMetadataChanges,
    ]);
};

const noUserMessage = "Can not update current user as current user is not set";
const useUpdateMyProfile = () => {
    const { firestore, user } = useSUBContext();
    return useCallback(({ displayName }) => __awaiter(void 0, void 0, void 0, function* () {
        if (!user || !firestore) {
            return Promise.reject(noUserMessage);
        }
        yield updateProfile(user, { displayName });
        yield updateDoc(doc(firestore, usersCollectionKey, user.uid), {
            displayName,
        });
    }), [firestore, user]);
};
/*
 * NOTE: useGetUser is a lot like useAuth, but instead of returning the saved
 * and stored user data it returns a function to take a snapshot of the
 * currently signed in user and returns the data in a promise. Unless you need
 * to make sure that you are getting the very latest data, you probably want
 * useAuth instead.
 */
const useGetUser = () => {
    const { firestore, auth } = useSUBContext();
    return useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!firestore || !(auth === null || auth === void 0 ? void 0 : auth.currentUser))
            return null;
        const docRef = doc(firestore, usersCollectionKey, auth.currentUser.uid);
        const snap = yield getDoc(docRef);
        return snap.data();
    }), [firestore, auth === null || auth === void 0 ? void 0 : auth.currentUser]);
};
const useObserveUser = (opts) => {
    const { user } = useSUBContext();
    useObserveDocument(Object.assign(Object.assign({}, opts), { docKey: user === null || user === void 0 ? void 0 : user.uid, collectionKey: usersCollectionKey }));
};
const useUserDoc = () => {
    const [userDoc, setUserDoc] = useState(null);
    useObserveUser({ onUpdate: setUserDoc });
    return userDoc;
};

const createMemberPath = (orgId) => {
    return [
        organizationsCollectionKey,
        orgId,
        membersCollectionKey,
    ].join("/");
};
/*
 * Returns the member document for the currently logged in user. This contains
 * organization specific information for said user.
 */
const useCurrentOrgMember = () => {
    const [member, setMember] = useState(null);
    const userDoc = useUserDoc();
    const { uid = "", currentOrganization = "", } = userDoc || {};
    const memberPath = createMemberPath(currentOrganization);
    useObserveDocument({
        docKey: uid,
        onUpdate: setMember,
        collectionKey: memberPath,
    });
    return member || null;
};

const useUserHasRoles = (roles) => {
    const member = useCurrentOrgMember();
    const memberRoles = (member === null || member === void 0 ? void 0 : member.roles) || [];
    return roles.some((role) => memberRoles.includes(role));
};

const useIsOrgAdmin = () => {
    return useUserHasRoles(["admin"]);
};

/*
 * Returns a domain to attach the login cookie to. Note that this domain must
 * be at least two segments for cookie subdomain rules to work.
 * The domain returned is the current domain minus one subdomain attribute.
 * So, if you are on `sso.example.com` the value returned will be
 * `.example.com` which will make the cookie available on `app.example.com` or
 * `web.example.com` as well.
 */
const getDomain = () => {
    const domain = window.location.hostname.split(".").slice(1).join(".");
    return "." + domain;
};
const setCookie = ({ name, value, expiration, }) => {
    const domain = `Domain=${getDomain()}`;
    const sameSite = "SameSite=Strict";
    const cookieValue = `${name}=${value}`;
    document.cookie =
        `${cookieValue}; ${domain}; ${expiration}; ${sameSite}; path=/;`;
};
const getCookie = (name) => {
    var _a;
    const cookies = (_a = document.cookie) === null || _a === void 0 ? void 0 : _a.split("; ");
    const cookie = cookies === null || cookies === void 0 ? void 0 : cookies.find((c) => c.startsWith(name));
    if (!cookie)
        return null;
    return cookie.split("=")[1];
};
const setLoginCookie = (name, value) => {
    const expiration = "max-age=432000";
    setCookie({ name, value, expiration });
};
const deleteLoginCookie = (name) => {
    const expiration = "expires=Fri, 27 Jul 2001 02:47:11 UTC";
    setCookie({ name, expiration });
};
/*
 * Given an object `skeleton` and an object `obj`, `checkObjType` will compare
 * the keys and values of the two to try to determine if `obj` is of the same
 * shape as `skeleton`, and thus can be treated as the same type. This
 * function will be curried if it recieves only one argument.
 * Note that this utility function is somewhat limited in it's application as
 * it will not work on objects with nullable fields.
 */
const checkObjType = curry((skeleton, obj) => {
    return Object.entries(skeleton).reduce((acc, [key, value]) => {
        const keyTypeMatches = typeof value === typeof obj[key];
        return keyTypeMatches && acc;
    }, true);
});
// This is a temporary function. I'm going to look at getting a real logger in
// here at some point to handle stuff like this.
const logIfDebug = (...details) => {
    const shouldLog = window.localStorage.getItem("debugLog");
    if (shouldLog === "true") {
        console.log(...details);
    }
};
const getUnverifiedTokenOrNull = (token) => {
    if (!token)
        return null;
    try {
        return jwtDecode(token);
    }
    catch (e) {
        console.error(e);
        return null;
    }
};

const tokenKey = "googleToken";
const getToken = () => localStorage.getItem(tokenKey);
const tokenRegex = /id_token/;
const checkIfRedirected = () => Boolean(window.location.hash.match(tokenRegex));
const isExpired = (user) => {
    const exp = new Date(user.exp * 1000);
    const now = new Date();
    return exp < now;
};
const isGoogleUser = (user) => {
    const check = checkObjType({
        exp: 1,
        email: "",
    });
    return check(user);
};
/*
 * useInitGoogleCtx is meant for internal use by this library only. Given
 * valid configuration options, it will initialize all the core requirements
 * for managing Google related auth state.
 */
const useInitGoogleCtx = ({ clientId, hostedDomain, uxMode = "redirect", }) => {
    const [user, setUser] = useState(null);
    const [googleJwt, setGoogleJwt] = useState(getToken);
    const didRedirect = checkIfRedirected();
    const onFailure = console.error;
    const { signIn: googleSignIn, loaded } = useGoogleLogin({
        uxMode,
        clientId,
        onFailure,
        hostedDomain,
    });
    const setToken = useCallback((value) => {
        setGoogleJwt(value);
        localStorage.setItem(tokenKey, value);
    }, [setGoogleJwt]);
    const signOut = useCallback(() => {
        localStorage.setItem(tokenKey, "");
        setUser(null);
        setGoogleJwt(null);
    }, [setUser, setGoogleJwt]);
    useEffect(() => {
        try {
            if (typeof googleJwt !== "string")
                return setUser(null);
            const token = jwtDecode(googleJwt);
            if (!isGoogleUser(token)) {
                setUser(null);
                return;
            }
            if (isExpired(token)) {
                googleSignIn();
                return;
            }
            if (token.exp !== (user === null || user === void 0 ? void 0 : user.exp)) {
                setUser(token);
                return;
            }
        }
        catch (e) {
            console.error(e);
            setUser(null);
        }
    }, [googleJwt, googleSignIn, setUser, setToken, user, loaded]);
    useEffect(() => {
        if (didRedirect) {
            const hash = window.location.hash.substring(1);
            const params = new URLSearchParams(hash);
            const idToken = params.get("id_token");
            if (idToken)
                setToken(idToken);
            history.pushState("", document.title, window.location.pathname);
        }
    }, [setToken, didRedirect]);
    return useMemo(() => ({
        user,
        loaded,
        setUser,
        signOut,
        setToken,
        googleJwt,
        googleSignIn,
        setGoogleJwt,
    }), [loaded, setToken, signOut, googleJwt, user, googleSignIn, setUser]);
};

const initialState = {
    user: null,
    loaded: false,
    setUser: noop,
    signOut: noop,
    googleJwt: null,
    setGoogleJwt: noop,
    googleSignIn: noop,
};
const GoogleContext = React.createContext(initialState);
const GoogleContextProvider = ({ config, children, }) => {
    const ctxValue = useInitGoogleCtx(config);
    return (React.createElement(GoogleContext.Provider, { value: ctxValue }, children));
};

const useGoogleContext = () => {
    return useContext(GoogleContext);
};

const useIsSuperAdmin = () => {
    const { user: googleUser, loaded: googleLoaded } = useGoogleContext();
    return Boolean(googleLoaded && googleUser);
};

const RoleGuard = ({ roles, NoAuth, children, }) => {
    const isSuperAdmin = useIsSuperAdmin();
    const isOrgAdmin = useIsOrgAdmin();
    const hasAtLeastOne = useUserHasRoles(roles);
    if (isSuperAdmin || isOrgAdmin || hasAtLeastOne)
        return React.createElement(React.Fragment, null, children);
    if (typeof NoAuth === "function")
        return React.createElement(NoAuth, null);
    return NoAuth || null;
};

const SSOAuthBoundaryContext = React.createContext(null);

const unvalidated = "UNVALIDATED";
const valid = "VALID";
const invalid = "INVALID";
const signInUser = (opts) => __awaiter(void 0, void 0, void 0, function* () {
    const { auth, client, mutation, setStatus, authCookieName } = opts;
    const authCookie = getCookie(authCookieName);
    if (!authCookie)
        return setStatus(invalid);
    const { data } = yield client.mutate({
        mutation,
        variables: { authCookie },
    });
    if (!(data === null || data === void 0 ? void 0 : data.validateUser))
        return setStatus(invalid);
    const unvalidatedJwt = getUnverifiedTokenOrNull(data.validateUser);
    if (unvalidatedJwt === null || unvalidatedJwt === void 0 ? void 0 : unvalidatedJwt.tenant_id) {
        auth.tenantId = unvalidatedJwt.tenant_id;
    }
    yield signInWithCustomToken(auth, data.validateUser);
    setStatus(valid);
});
const validate = (opts) => {
    const { auth, setStatus } = opts;
    const currentUser = auth.currentUser;
    if (currentUser)
        return setStatus(valid);
    return signInUser(opts);
};
const useFirebaseValidation = ({ gql, client, }) => {
    const { auth, user, authHasLoaded, authCookieName } = useSUBContext();
    const [status, setStatus] = useState(unvalidated);
    const tokenMutation = useMemo(() => {
        return gql `
      mutation ValidateUser($authCookie: String!) {
        validateUser(authCookie: $authCookie)
      }
    `;
    }, [gql]);
    const cookieMutation = useMemo(() => {
        return gql `
      mutation CreateCookie {
        createCookie
      }
    `;
    }, [gql]);
    useEffect(() => {
        logIfDebug(auth, user, authHasLoaded);
        if (!auth || !authHasLoaded)
            return;
        if (user)
            return setStatus(valid);
        validate({
            auth,
            client,
            setStatus,
            authCookieName,
            mutation: tokenMutation,
        });
    }, [
        user,
        auth,
        client,
        setStatus,
        tokenMutation,
        authHasLoaded,
        authCookieName,
    ]);
    useEffect(() => {
        if (!auth)
            return;
        const requestCookie = () => __awaiter(void 0, void 0, void 0, function* () {
            const { data } = yield client.mutate({
                mutation: cookieMutation,
            });
            setLoginCookie(authCookieName, data.createCookie);
        });
        if (user)
            requestCookie();
    }, [auth, client, authCookieName, cookieMutation, user]);
    return status;
};

const useSSOAuthBoundaryContext = () => {
    return useContext(SSOAuthBoundaryContext);
};

const useAuth = () => {
    return useSUBContext().user;
};

const invalidAuthMessage = "Invalid Firebase Auth object.";
const useSignin = () => {
    const { setUser, firestore, auth } = useSUBContext();
    return useCallback((email, password) => __awaiter(void 0, void 0, void 0, function* () {
        if (!auth || !firestore)
            return;
        const response = yield signInWithEmailAndPassword(auth, email, password);
        const { user } = response;
        const userRef = doc(firestore, usersCollectionKey, user.uid);
        const userDoc = yield getDoc(userRef);
        if (userDoc.exists()) {
            yield updateDoc(userRef, { lastSignInTime: user.metadata.lastSignInTime });
            setUser(user);
            return user;
        }
        yield setDoc(doc(firestore, usersCollectionKey, user.uid), {
            email: user.email,
            displayName: user.displayName,
            emailVerified: user.emailVerified,
            createdAt: user.metadata.creationTime,
            lastSignInTime: user.metadata.lastSignInTime,
        });
        return user;
    }), [setUser, auth, firestore]);
};
const useSignOut = (shouldInvalidate = true) => {
    const { setUser, auth, authCookieName } = useSUBContext();
    const ssoContext = useSSOAuthBoundaryContext();
    const invalidate = useSetSessionInvalidation();
    return useCallback(() => {
        var _a;
        deleteLoginCookie(authCookieName);
        if (shouldInvalidate)
            (_a = invalidate()) === null || _a === void 0 ? void 0 : _a.catch(console.error);
        const signoutPromise = auth ?
            signOut(auth) :
            Promise.reject(invalidAuthMessage);
        if (ssoContext && (auth === null || auth === void 0 ? void 0 : auth.currentUser)) {
            const { gql, client } = ssoContext;
            const mutation = gql `
        mutation InvalidateSession {
          invalidateSession
        }
      `;
            client.mutate({ mutation });
        }
        return signoutPromise.then(() => setUser(null))
            .catch(console.error);
    }, [setUser, auth, authCookieName, ssoContext, invalidate, shouldInvalidate]);
};
const useCreateInitialUserDoc = () => {
    const { firestore } = useSUBContext();
    return useCallback((user) => __awaiter(void 0, void 0, void 0, function* () {
        if (!firestore) {
            console.error("No Firestore Instance Provided");
            return null;
        }
        const userRef = doc(firestore, usersCollectionKey, user.uid);
        const userDoc = yield getDoc(userRef);
        if (userDoc.exists())
            return user;
        yield setDoc(userRef, {
            email: user.email,
            displayName: user.displayName,
            emailVerified: user.emailVerified,
            createdAt: user.metadata.creationTime || new Date(),
        });
        return user;
    }), [firestore]);
};
const useSignup = () => {
    const { auth } = useSUBContext();
    const createDoc = useCreateInitialUserDoc();
    return useCallback(({ email, password, validationUrl }) => __awaiter(void 0, void 0, void 0, function* () {
        if (!auth) {
            console.error(invalidAuthMessage);
            return null;
        }
        const actionCodeSettings = {
            url: validationUrl,
            handleCodeInApp: true,
        };
        const response = yield createUserWithEmailAndPassword(auth, email, password);
        const { user } = response;
        if (user) {
            yield createDoc(user);
            yield sendEmailVerification(user, actionCodeSettings);
        }
        return user;
    }), [auth, createDoc]);
};
/*
 * useSetSessionInvalidation and useObserveUserLogout are used together to
 * invalidate a user's session when they log out in the context of SSO.
 */
const useSetSessionInvalidation = () => {
    const { firestore } = useSUBContext();
    const user = useAuth();
    return useCallback(() => {
        if (!user || !firestore)
            return;
        const userRef = doc(firestore, usersCollectionKey, user.uid);
        return updateDoc(userRef, { lastSessionInvalidation: Number(new Date()) });
    }, [firestore, user]);
};
const useObserveUserLogout = () => {
    const { auth } = useSUBContext();
    const appSignOut = useSignOut(false);
    useObserveUser({
        onUpdate: (user) => {
            var _a;
            (_a = auth === null || auth === void 0 ? void 0 : auth.currentUser) === null || _a === void 0 ? void 0 : _a.getIdTokenResult().then((token) => {
                const lastInvalidation = user === null || user === void 0 ? void 0 : user.lastSessionInvalidation;
                if (!lastInvalidation)
                    return;
                const iat = new Date(token.issuedAtTime);
                const invalidationTime = new Date(lastInvalidation);
                if (iat < invalidationTime) {
                    appSignOut();
                }
            });
        },
    });
};

/*
 * Allows for using FireSUB on subdomains via an SSO solution.
 */
const SSOAuthBoundary = ({ gql, client, children, InvalidPlaceHolder, LoadingPlaceHolder, }) => {
    const validationState = useFirebaseValidation({ gql, client });
    const contextState = useMemo(() => ({ gql, client }), [gql, client]);
    useObserveUserLogout();
    logIfDebug("validationState", validationState);
    switch (validationState) {
        case invalid:
            return InvalidPlaceHolder ? React.createElement(InvalidPlaceHolder, null) : React.createElement("div", null);
        case valid:
            return (React.createElement(SSOAuthBoundaryContext.Provider, { value: contextState }, children));
        default: // Covers case unvalidated...
            return LoadingPlaceHolder ? React.createElement(LoadingPlaceHolder, null) : React.createElement("div", null);
    }
};

const SUBAuthBoundary = ({ children, PlaceHolder, LoadingPlaceHolder, UnverifiedPlaceHolder, }) => {
    const { user, authHasLoaded } = useSUBContext();
    if (!authHasLoaded) {
        return LoadingPlaceHolder ? React.createElement(LoadingPlaceHolder, null) : React.createElement("div", null);
    }
    if (authHasLoaded && !user) {
        return PlaceHolder ? React.createElement(PlaceHolder, null) : React.createElement("div", null);
    }
    if (UnverifiedPlaceHolder && user && !user.emailVerified) {
        return React.createElement(UnverifiedPlaceHolder, null);
    }
    return React.createElement(React.Fragment, null, children);
};

/*
 * GoogleAuthBoundary provides a method for ensuring that a user is
 * authenticated via Google prior to displaying any data lower in the render
 * tree. An example value of the `PlaceHolder` prop may be a login page. See
 * playground page for example.
 * /playground/src/components/pages/GoogleLogin/index.tsx
 * http://localhost:3000/googleLogin
 */
const GoogleAuthBoundary = ({ children, PlaceHolder, LoadingPlaceHolder, }) => {
    const { user, loaded } = useGoogleContext();
    if (!loaded) {
        return LoadingPlaceHolder ? React.createElement(LoadingPlaceHolder, null) : React.createElement("div", null);
    }
    // If we don't have a user yet, return a placeholder.
    if (!user) {
        return PlaceHolder ? React.createElement(PlaceHolder, null) : React.createElement("div", null);
    }
    return React.createElement(React.Fragment, null, children);
};

const fetchMembersForOrg = (firestore, orgUid) => __awaiter(void 0, void 0, void 0, function* () {
    const members = collection(firestore, organizationsCollectionKey, orgUid, "members");
    const membersSnap = yield getDocs(members);
    return membersSnap.docs.map((snap) => (Object.assign({ uid: snap.id }, snap.data())));
});
const useOrgMembers = (orgUid) => {
    const { firestore } = useSUBContext();
    const [members, setMembers] = useState([]);
    useEffect(() => {
        if (!orgUid || !firestore)
            return;
        fetchMembersForOrg(firestore, orgUid).then((memberList) => {
            setMembers(memberList);
        });
    }, [orgUid, setMembers, firestore]);
    return members;
};

const noConfigProviders = {
    EmailAuthProvider: EmailAuthProvider,
    FacebookAuthProvider: FacebookAuthProvider,
    GithubAuthProvider: GithubAuthProvider,
    GoogleAuthProvider: GoogleAuthProvider,
    TwitterAuthProvider: TwitterAuthProvider,
};
const configRequiredProviders = {
    OAuthProvider: OAuthProvider,
    SAMLAuthProvider: SAMLAuthProvider,
};
const authProviderMappings = Object.assign(Object.assign({}, noConfigProviders), configRequiredProviders);
// To keep things backwards compatible, default to just an email provider if
// none have been set.
const defaultProviders = [
    { providerType: "EmailAuthProvider", name: "Email" },
];
const mapToProvider = (def) => {
    if (Object.keys(configRequiredProviders).includes(def.providerType)) {
        const oauthDef = def;
        const Mapping = configRequiredProviders[oauthDef.providerType];
        const provider = new Mapping(oauthDef.config);
        return { def, provider };
    }
    const noConfigDef = def;
    const NoConfigMapping = noConfigProviders[noConfigDef.providerType];
    const provider = new NoConfigMapping();
    return { def, provider };
};
const useAuthProvider = (def) => {
    if (!def)
        return null;
    return useMemo(() => mapToProvider(def), [def]);
};
const useAuthProviders = (defs) => {
    return useMemo(() => {
        if (!(defs === null || defs === void 0 ? void 0 : defs.length))
            return defaultProviders.map(mapToProvider);
        return defs.map(mapToProvider);
    }, [defs]);
};

/*
 * useSearchDocuments can be used to perform generic searches on any first
 * level collection contained in the `firestore` instances. If there is no
 * `firestore` instances, it returns early with an empty array.
 * Note that `QueryOpts` corespond to the expectations of the `where` function
 * provided by `firebase/firestore`. Example usage of where can be found at
 * the link below:
 * https://firebase.google.com/docs/firestore/query-data/queries#simple_queries
 */
const useSearchDocuments = (collectionKey) => {
    const { firestore } = useSUBContext();
    return useCallback((...queries) => __awaiter(void 0, void 0, void 0, function* () {
        if (!firestore)
            return [];
        const collectionRef = collection(firestore, collectionKey);
        const whereStatements = queries.map((opts) => where(...opts));
        const activeQuery = query(collectionRef, ...whereStatements);
        const results = yield getDocs(activeQuery);
        return results.docs.map((doc) => (Object.assign({ uid: doc.ref.id }, doc.data())));
    }), [firestore, collectionKey]);
};

const useSearchOrganizations = () => {
    return useSearchDocuments(organizationsCollectionKey);
};
const useGetMyOrganizations = () => {
    const user = useAuth();
    const searchOrganizations = useSearchOrganizations();
    return useCallback((...queries) => __awaiter(void 0, void 0, void 0, function* () {
        if (!user)
            return [];
        const userQuery = [
            "memberUids",
            "array-contains",
            user.uid,
        ];
        return searchOrganizations(userQuery, ...queries);
    }), [user, searchOrganizations]);
};
const useGetOrganization = (id) => {
    const { firestore } = useSUBContext();
    return useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!firestore)
            return null;
        const orgRef = doc(firestore, organizationsCollectionKey, id);
        const snap = yield getDoc(orgRef);
        if (snap.exists())
            return snap.data();
        return null;
    }), [id, firestore]);
};
const useUpdateOrganization = (id) => {
    const { firestore } = useSUBContext();
    return useCallback((updates) => __awaiter(void 0, void 0, void 0, function* () {
        if (!firestore)
            return null;
        const orgRef = doc(firestore, organizationsCollectionKey, id);
        yield updateDoc(orgRef, updates);
    }), [id, firestore]);
};
const useAddUserToOrganization = (orgId) => {
    const { firestore } = useSUBContext();
    return useCallback((userUid, roles = ["user"]) => __awaiter(void 0, void 0, void 0, function* () {
        if (!firestore)
            return null;
        const orgRef = doc(firestore, organizationsCollectionKey, orgId);
        const data = { roles };
        return yield runTransaction(firestore, (tx) => __awaiter(void 0, void 0, void 0, function* () {
            tx.update(orgRef, { memberUids: arrayUnion(userUid) });
            tx.set(doc(orgRef, "members", userUid), data);
        }));
    }), [orgId, firestore]);
};
const useCurrentOrganization = () => {
    const [orgDoc, setOrgDoc] = useState(null);
    const user = useUserDoc();
    const getOrg = useGetOrganization((user === null || user === void 0 ? void 0 : user.currentOrganization) || "");
    useEffect(() => {
        if (!(user === null || user === void 0 ? void 0 : user.currentOrganization))
            return;
        getOrg().then((org) => setOrgDoc(org));
    }, [user, getOrg, setOrgDoc]);
    return orgDoc;
};

const useLinkAuthProvider = ({ provider, redirect = false, }) => {
    const user = useAuth();
    const linkAuthProvider = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!user)
            return Promise.reject(new Error("No user to link to"));
        const linker = redirect ? linkWithRedirect : linkWithPopup;
        return linker(user, provider);
    }), [provider, user, redirect]);
    return linkAuthProvider;
};

const msg = "No Auth available at call to signInWithPopup";
const useAuthProviderLogin = ({ provider, redirect = false, }) => {
    const { auth } = useSUBContext();
    const createDoc = useCreateInitialUserDoc();
    const login = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!auth)
            return Promise.reject(new Error(msg));
        const signer = redirect ? signInWithRedirect : signInWithPopup;
        // NOTE: If we are redirecting, the page will be shut down and nothing
        // will happen after this next function call as the user will be on a
        // different website.
        const userCred = yield signer(auth, provider);
        const additionalUserInfo = yield getAdditionalUserInfo(userCred);
        if (additionalUserInfo === null || additionalUserInfo === void 0 ? void 0 : additionalUserInfo.isNewUser) {
            yield createDoc(userCred.user);
        }
        return userCred;
    }), [provider, auth, redirect, createDoc]);
    return login;
};

const useCurrentOrgMembers = () => {
    const user = useUserDoc();
    return useOrgMembers(user === null || user === void 0 ? void 0 : user.currentOrganization);
};

export { GoogleAuthBoundary, GoogleContext, GoogleContextProvider, RoleGuard, SSOAuthBoundary, SUBAuthBoundary, SUBContext, SUBContextProvider, authProviderMappings, invalid, unvalidated, useAddUserToOrganization, useAuth, useAuthProvider, useAuthProviderLogin, useAuthProviders, useCreateInitialUserDoc, useCurrentOrgMember, useCurrentOrgMembers, useCurrentOrganization, useFirebaseValidation, useGetMyOrganizations, useGetOrganization, useGetUser, useGoogleContext, useIsOrgAdmin, useIsSuperAdmin, useLinkAuthProvider, useObserveDocument, useObserveUser, useObserveUserLogout, useOrgMembers, useSUBContext, useSearchDocuments, useSearchOrganizations, useSetSessionInvalidation, useSignOut, useSignin, useSignup, useUpdateMyProfile, useUpdateOrganization, useUserDoc, valid };
